import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { fetchData, endSession } from '../../helpers/methods';
import { UserContext } from '../../contexts/user-context';
import { CustomerContext } from '../../contexts/customer-context';
import { CustomerBrandingContext } from '../../contexts/customer-branding-context';

import Navigation from '../../component-library/navigation';
import NavigationMobile from '../../component-library/navigation-mobile';
import ButtonIcon from '../../component-library/button-icon';
import SpinnerContainer from '../../component-library/spinner-container';
import defaultLogo from '../../../../images/logo.png';
import IgniteAiSearchButton from '../../ai/IgniteAiSearchButton';
import IgniteAiSearch from '../../ai/IgniteAiSearch';

const Header = () => {
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const {
		givenName,
		preferredUsername,
		claims: userDataRoles = [],
	} = useContext(UserContext);
	const {
		search: {
			taxonomies: customerTaxonomyData,
		} = {},
		portalUIConfig: {
			mainNavigation: mainNavUiConfig = {},
		} = {},
		portalAdminUIConfig: {
			aiAccess: aiAccessUIConfig = {},
		} = {},
	} = useContext(CustomerContext);
	const {
		brandName,
	} = useContext(CustomerBrandingContext);
	const [loadingNotifications, setLoadingNotifications] = useState(false);
	const [showUserMenu, setShowUserMenu] = useState(false);
	const [showMobileMenu, setShowMobileMenu] = useState(false);
	const [showNotifications, setShowNotifications] = useState(false);
	const [notificationData, setNotificationData] = useState([]);
	const [repliesData, setRepliesData] = useState([]);
	const [announcementsData, setAnnouncementsData] = useState([]);
	const [navigationData, setNavigationData] = useState([]);
	const [loadingLogo, setLoadingLogo] = useState(true);
	const [logo, setLogo] = useState(null);
	const [aiSearchOpen, setAiSearchOpen] = useState(false);

	const checkAiPermissions = () => {
		const isEnabled = aiAccessUIConfig?.enable !== false;
		const allowedRoles = aiAccessUIConfig?.roles || [];
		const hasAllowedRole = userDataRoles.some(r => allowedRoles.includes(r));
		const allowedUsers = aiAccessUIConfig?.users || [];
		const isAllowedUser = allowedUsers.includes(preferredUsername.toLowerCase());

		if (isEnabled && (hasAllowedRole || isAllowedUser)) {
			return true;
		}
		return false;
	};

	const aiAccess = checkAiPermissions();

	const getNavigationData = () => {
		const newNavigationData = [
			{ name: 'Home', url: '/' },
			{ name: 'Catalog', url: '/catalog' },
			{ name: 'My Favorites', url: '/catalog/favorites' },
			{ name: 'Latest News', url: '/news/announcements' },
		];
		if (mainNavUiConfig?.topApis?.enabled !== false) {
			newNavigationData.splice(2, 0, { name: 'Top APIs', url: '/catalog/top-apis' });
		}
		if (mainNavUiConfig?.taxonomy?.enabled !== false && customerTaxonomyData?.length > 0) {
			newNavigationData.splice(2, 0, { name: 'Taxonomy', url: '/catalog/taxonomy' });
		}
		setNavigationData(newNavigationData);
	};

	const getNotificationData = () => {
		setLoadingNotifications(true);
		fetchData({
			uri: '/api/web/my/store',
			method: 'GET',
		})
			.then(res => {
				const newNotificationData = res.body.notifications;
				const newRepliesData = res.body.replies;
				const newAnnouncementsData = res.body.announcements;
				setNotificationData(newNotificationData);
				setRepliesData(newRepliesData);
				setAnnouncementsData(newAnnouncementsData);
				setLoadingNotifications(false);
			})
			.catch(() => {
				setLoadingNotifications(false);
			});
	};

	const clearAllNotifications = () => {
		setShowNotifications(false);
		fetchData({
			uri: '/api/web/my/store/notifications/clear',
			method: 'PUT',
		})
			.then(() => {
				getNotificationData();
			});
	};

	const clearNotification = id => {
		fetchData({
			uri: `/api/web/my/store/notifications/${id}/clear`,
			method: 'PUT',
		})
			.then(() => {
				getNotificationData();
			});
	};

	const clearAllReplies = () => {
		setShowNotifications(false);
		fetchData({
			uri: '/api/web/my/store/replies/clear',
			method: 'PUT',
		})
			.then(() => {
				getNotificationData();
			});
	};

	const clearReply = (specId, branches) => {
		const lastBranch = branches.slice(-1);
		const branchKey = lastBranch[0].key;
		fetchData({
			uri: `/api/web/my/store/replies/${specId}/${branchKey}/clear`,
			method: 'PUT',
		})
			.then(() => {
				getNotificationData();
			});
	};

	const viewNotification = id => {
		fetchData({
			uri: `/api/web/my/store/notifications/${id}/clear`,
			method: 'PUT',
		})
			.then(() => {
				setShowNotifications(false);
				navigate(`./catalog/details/${id}/provider-updates`);
			})
			.catch(() => {
				setShowNotifications(false);
				navigate(`./catalog/details/${id}/provider-updates`);
			});
	};

	const viewReply = (id, convKey, order) => {
		setShowNotifications(false);
		navigate(`./catalog/details/${id}/contact-provider/${convKey}/${order}`);
	};

	const getNotificationBadgeValue = value => {
		if (value > 99) {
			return '99+';
		}
		return value;
	};

	const handleViewProfile = () => {
		setShowUserMenu(false);
		navigate('/profile');
	};

	const getLogo = () => {
		const uri = '/api/web/config/branding/logo';
		fetchData({
			uri,
			method: 'GET',
		})
			.then(({ ok, body }) => {
				if (ok) {
					setLogo(body);
					setLoadingLogo(false);
				} else {
					setLogo(defaultLogo);
					setLoadingLogo(false);
				}
			})
			.catch(() => {
				setLogo(defaultLogo);
				setLoadingLogo(false);
			});
		return null;
	};

	useEffect(() => {
		getNotificationData();
	}, [pathname]);

	useEffect(() => {
		getLogo();
		getNavigationData();
	}, []);

	return (
		<>
			<header className="header flex-row justify-space-between align-items-center">
				<div className="flex-row justify-flex-start align-items-center">
					<div className="navigation-mobile__icon margin-right-16">
						<ButtonIcon
							icon="bars"
							title="Menu"
							onClick={() => setShowMobileMenu(!showMobileMenu)}
						/>
					</div>
					{!loadingLogo && (
						<>
							{!loadingLogo && (
								<img src={logo} className="header__logo" alt={brandName} role="presentation" onClick={() => navigate('/')} />
							)}
						</>
					)}
				</div>
				<div className="flex-row align-items-center">
					<div className="navigation__container">
						<Navigation
							data={navigationData}
							announcementsData={announcementsData}
						/>
					</div>
					{aiAccess && (
						<div className="ignite-ai__header-search-button-container border-left padding-horizontal-16">
							<IgniteAiSearchButton
								openSearch={() => setAiSearchOpen(true)}
							/>
						</div>
					)}
					<div className="header__notifications flex-row align-items-center border-left padding-horizontal-8">
						<ButtonIcon
							icon="bell"
							classes="margin-horizontal-4"
							onClick={() => setShowNotifications(!showNotifications)}
						/>
						{notificationData?.length > 0 || repliesData?.length > 0 ? (
							<div className="header__notification-badge">{getNotificationBadgeValue((notificationData.length + repliesData.length))}</div>
						) : null}
						{showNotifications && (
							<div
								className="header__notification-list"
								onMouseLeave={() => setShowNotifications(false)}
							>
								{loadingNotifications && (
									<SpinnerContainer padding={false} />
								)}
								{!loadingNotifications && (
									<>
										<div className="padding-vertical-8 margin-bottom-4 border-bottom grey-text">NOTIFICATIONS</div>
										{notificationData?.length > 0 ? (
											notificationData.map((notification, index) => (
												// eslint-disable-next-line react/no-array-index-key
												<div className="flex-row justify-space-between align-items-center padding-bottom-8 margin-bottom-8 border-bottom" key={index}>
													<div
														className="header__notification"
														role="presentation"
														onClick={() => viewNotification(notification.id)}
													>
														<strong>{notification.name}</strong>
														<p className="margin-bottom-none">A new provider update has been posted.</p>
													</div>
													<div>
														<ButtonIcon
															icon="xmark"
															title="Clear Notification"
															onClick={() => clearNotification(notification.id)}
														/>
													</div>
												</div>
											))
										) : (
											<div className="text-center grey-text margin-top-16 margin-bottom-24">No notifications</div>
										)}
										{notificationData?.length > 0 && (
											<div className="text-right">
												<ButtonIcon
													icon="xmark"
													label="Clear All Notifications"
													classes="margin-top-8 margin-bottom-16"
													iconRight
													onClick={() => clearAllNotifications()}
												/>
											</div>
										)}
										<div className="padding-vertical-8 margin-bottom-4 border-bottom border-top grey-text">REPLIES</div>
										{repliesData?.length > 0 ? (
											repliesData.map((reply, index) => (
												// eslint-disable-next-line react/no-array-index-key
												<div className="flex-row justify-space-between align-items-center padding-bottom-8 margin-bottom-8 border-bottom" key={index}>
													<div
														className="header__notification"
														role="presentation"
														onClick={() => viewReply(
															reply.id,
															reply.conversationBranches[0].key,
															reply.conversationBranches[0].order,
														)}
													>
														<strong>{reply.name}</strong>
														<p className="margin-bottom-none">
															{`You have a new reply to your ${reply.conversationBranches[0].styling}:`}
															<br />
															<em>
																&quot;
																{`${reply.conversationBranches[0].messageStub}${reply.conversationBranches[0].stubbed ? '...' : ''}`}
																&quot;
															</em>
														</p>
													</div>
													<div>
														<ButtonIcon
															icon="xmark"
															title="Clear Notification"
															onClick={() => clearReply(reply.id, reply.conversationBranches)}
														/>
													</div>
												</div>
											))
										) : (
											<div className="text-center grey-text margin-top-16">No replies</div>
										)}
										{repliesData?.length > 0 && (
											<div className="text-right">
												<ButtonIcon
													icon="xmark"
													label="Clear All Replies"
													classes="margin-top-8"
													iconRight
													onClick={() => clearAllReplies()}
												/>
											</div>
										)}
									</>
								)}
							</div>
						)}
					</div>
					<div className="header__user-menu flex-row align-items-center border-left padding-left-8">
						<ButtonIcon
							icon="user"
							label={`Hi, ${givenName || 'User'}`}
							classes="margin-left-4"
							onClick={() => {}}
							onMouseEnter={() => setShowUserMenu(true)}
						/>
						{showUserMenu && (
							<div
								className="header__user-menu-options"
								onMouseLeave={() => setShowUserMenu(false)}
							>
								<div className="margin-bottom-8 padding-bottom-8 border-bottom">
									<ButtonIcon
										icon="user"
										label="My Profile"
										isSmall
										onClick={() => handleViewProfile()}
									/>
								</div>
								<div>
									<ButtonIcon
										icon="key"
										label="Logout"
										isSmall
										onClick={() => endSession()}
									/>
								</div>
							</div>
						)}
					</div>
				</div>
			</header>
			{showMobileMenu && (
				<NavigationMobile
					data={navigationData}
					announcementsData={announcementsData}
					showMobileMenu={() => setShowMobileMenu(false)}
				/>
			)}
			<IgniteAiSearch
				handleClose={() => setAiSearchOpen(false)}
				isOpen={aiSearchOpen}
				userName={givenName}
			/>
		</>
	);
};

export default Header;
